import { Box, Chip, ClickAwayListener, Paper, Popper, Tooltip, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deletePortfolioById, getPortfoliosData, getStatusConfig } from '../../../redux/slices/transitions';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../reusable-components/iconify';
import CustomToolbar from './CustomToolbar';
import MobilePortfolioList from './MobilePortfolioList';

export default function Transitions() {
  const navigate = useNavigate();
  const [menuFacilities, setMenuFacilities] = useState([]);
  const dispatch = useDispatch();
  const {
    data: { portfolios, statusConfig },
  } = useSelector((state) => state.transitions);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const anchorRef = useRef(null);

  useEffect(() => {
    dispatch(getStatusConfig()).finally(() => setIsLoading(false));
    dispatch(getPortfoliosData());
  }, []);

  useEffect(() => {
    setRows(portfolios.map((p) => ({ ...p, isOpen: false })));
  }, [portfolios]);

  const handleFacilityListClick = (event, params) => {
    if (anchorRef.current === event.currentTarget) {
      anchorRef.current = null;
      setRows((prev) => prev.map((row) => ({ ...row, isOpen: false })));
    } else if (params?.value?.length > 0) {
      event.stopPropagation();
      anchorRef.current = event.currentTarget;
      setRows((prev) =>
        prev.map((row) => (row.id === params?.row?.id ? { ...row, isOpen: true } : { ...row, isOpen: false }))
      );
      setMenuFacilities(params.value);
    }
  };

  const ActionButtons = (params) => {
    const { id, isAdmin } = params.row;
    return (
      <Tooltip arrow title="Insufficient permissions for this portfolio" disableHoverListener={isAdmin}>
        <span>
          <ConfirmDialog
            disabled={!isAdmin}
            content={`Are you sure you want to delete this portfolio?`}
            action={() => dispatch(deletePortfolioById(id))}
          />
        </span>
      </Tooltip>
    );
  };

  const transitionColumns = [
    { field: 'portfolioName', headerName: 'Portfolio', minWidth: 0, flex: 1 },
    {
      field: 'transitionStatusId',
      headerName: 'Transition Status',
      renderCell: (params) => (
        <Chip
          variant="outlined"
          label={params.row.isCanceled ? 'Canceled' : statusConfig?.[params.value]?.statusDescription}
          color="secondary"
        />
      ),
      flex: 1,
      minWidth: 0,
    },
    {
      field: 'facilities',
      headerName: '# Facilities',
      flex: 1,
      minWidth: 0,
      sortComparator: (a, b) => (a?.length || 0) - (b?.length || 0),
      renderCell: (params) => (
        <Box
          onClick={(event) => handleFacilityListClick(event, params)}
          display="flex"
          alignItems="center"
          sx={{
            padding: 2,
            cursor: params?.value?.length > 0 ? 'pointer' : 'default',
            zIndex: 100000,
          }}
        >
          {params?.value?.length || 0}
          {params?.value?.length > 0 && (
            <Iconify icon={params?.row?.isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} sx={{ ml: 0.5 }} />
          )}
        </Box>
      ),
    },
    {
      field: 'dateAdded',
      headerName: 'Date Added',
      minWidth: 0,
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      disableReorder: true,
      width: 110,
      renderCell: (params) => <ActionButtons {...params} />,
      valueGetter: (params) => params.row.isAdmin,
      sortComparator: (a, b) => (b ? 1 : 0) - (a ? 1 : 0),
    },
  ];

  const CustomLeftToolbar = () => <CustomToolbar />;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  return (
    !isLoading &&
    (!isMobile ? (
      <>
        <CustomDataGrid
          gridId="transitions"
          boxSX={{ height: 'calc(100vh - 225px)' }}
          getRowHeight={() => 'auto'}
          data={rows}
          gridColumns={transitionColumns}
          applyQuickFilter
          loading={isLoading}
          onRowClick={(params) => {
            navigate(
              `/dashboard/transitions/${statusConfig?.[params.row.transitionStatusId]?.pageRouteName}/${
                params.row.id
              }?facilityId=${params.row.facilities?.[0]?.facilityId}`
            );
          }}
          CustomLeftToolbar={CustomLeftToolbar}
        />
        <Popper open={!!anchorRef.current} anchorEl={anchorRef.current} placement="bottom-start">
          <ClickAwayListener
            onClickAway={() => {
              anchorRef.current = null;
              setRows((prev) => prev.map((row) => ({ ...row, isOpen: false })));
            }}
          >
            <Paper sx={{ minWidth: 180, p: 1, boxShadow: 3 }}>
              {menuFacilities?.map((facility) => (
                <Box
                  key={facility.facilityId}
                  sx={{
                    cursor: 'default',
                    userSelect: 'none',
                    fontSize: '12px',
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {facility.facilityName}
                </Box>
              ))}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    ) : (
      <MobilePortfolioList />
    ))
  );
}
